import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FileUpload from './FileUpload';
import GalleryUpload from './GalleryUpload';
import { createPost, getCategories } from '../services/api';

const PostForm = ({ isAuthenticated, onAuthRequired }) => {
  const [formData, setFormData] = useState({
    name_or_title: '',
    context: '',
    short_description: '',
    featured_image: null,
    gallery: [],
    gallery_style: '1-column', // Default: 1 column
    content_category: '',
    car_manufacturer: '',
    car_brand: '',
    vehicle_location: '',
    engine_capacity: '',
    transmission: '',
    drivetrain: '',
    steering_wheel_position: '',
    mileage: '',
    vehicle_type: '',
    condition: '',
    colour: '',
    interior_color: '',
    manufacture_year: '',
    engine: '',
    rego: '',
    sell_status: '',
    comment_status: 'closed' // Default: comments off
  });
  
  // Toggle states for UI
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [resetTrigger, setResetTrigger] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [copyNotification, setCopyNotification] = useState('');
  const [existingShortcodes, setExistingShortcodes] = useState([]);
  const [shortcodeCopied, setShortcodeCopied] = useState(false);

  // Function to detect gallery shortcodes in content
  const detectShortcodes = (content) => {
    const shortcodeRegex = /\[gallery[^\]]*\]/g;
    const matches = content.match(shortcodeRegex) || [];
    return matches.map((shortcode, index) => ({
      id: `shortcode-${index}`,
      text: shortcode,
      isPlaceholder: shortcode.includes('GALLERY_PLACEHOLDER')
    }));
  };

  // Function to remove a specific shortcode from content
  const removeShortcode = (shortcodeToRemove) => {
    const updatedContent = formData.context.replace(shortcodeToRemove, '');
    setFormData(prev => ({
      ...prev,
      context: updatedContent
    }));
    
    // Reset copied state when manually removing shortcodes
    setShortcodeCopied(false);
    
    setCopyNotification('Shortcode removed!');
    setTimeout(() => setCopyNotification(''), 2000);
  };

  // Check if form has unsaved changes
  const checkForChanges = () => {
    // Don't check for changes during submission or upload
    if (isSubmitting || uploadProgress > 0) {
      return false;
    }
    
    const hasChanges = 
      formData.name_or_title.trim() !== '' ||
      formData.context.trim() !== '' ||
      formData.short_description.trim() !== '' ||
      formData.featured_image !== null ||
      (formData.gallery && formData.gallery.length > 0) ||
      formData.content_category !== '' ||
      formData.car_manufacturer.trim() !== '' ||
      formData.car_brand.trim() !== '' ||
      formData.vehicle_location.trim() !== '' ||
      formData.engine_capacity.trim() !== '' ||
      formData.transmission.trim() !== '' ||
      formData.drivetrain.trim() !== '' ||
      formData.steering_wheel_position.trim() !== '' ||
      formData.mileage.trim() !== '' ||
      formData.vehicle_type.trim() !== '' ||
      formData.condition.trim() !== '' ||
      formData.colour.trim() !== '' ||
      formData.interior_color.trim() !== '' ||
      formData.manufacture_year !== '' ||
      formData.engine.trim() !== '' ||
      formData.rego !== '' ||
      formData.sell_status.trim() !== '';
    
    setHasUnsavedChanges(hasChanges);
    return hasChanges;
  };

  // Handle beforeunload event - DISABLED to prevent browser warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Completely disabled - no browser warnings
      return;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Check for changes whenever form data changes
  useEffect(() => {
    checkForChanges();
  }, [formData]);

  // Detect shortcodes whenever content changes
  useEffect(() => {
    const shortcodes = detectShortcodes(formData.context);
    setExistingShortcodes(shortcodes);
  }, [formData.context]);

  // Handle reset with confirmation
  const handleReset = () => {
    if (checkForChanges()) {
      setPendingAction('reset');
      setShowConfirmModal(true);
    } else {
      performReset();
    }
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    if (checkForChanges()) {
      setPendingAction('cancel');
      setShowConfirmModal(true);
    } else {
      performCancel();
    }
  };

  // Perform reset
  const performReset = () => {
    setFormData({
      name_or_title: '',
      context: '',
      short_description: '',
      featured_image: null,
      gallery: [],
      gallery_style: '1-column',
      content_category: '',
      car_manufacturer: '',
      car_brand: '',
      vehicle_location: '',
      engine_capacity: '',
      transmission: '',
      drivetrain: '',
      steering_wheel_position: '',
      mileage: '',
      vehicle_type: '',
      condition: '',
      colour: '',
      interior_color: '',
      manufacture_year: '',
      engine: '',
      rego: '',
      sell_status: '',
      comment_status: 'closed'
    });
    setErrors({});
    setSubmitStatus(null);
    setUploadProgress(0);
    setUploadMessage('');
    setResetTrigger(prev => prev + 1);
    setHasUnsavedChanges(false);
    setShowConfirmModal(false);
    setPendingAction(null);
    setShowAdditionalFields(false);
  };

  // Perform cancel
  const performCancel = () => {
    setFormData({
      name_or_title: '',
      context: '',
      short_description: '',
      featured_image: null,
      gallery: [],
      gallery_style: '1-column',
      content_category: '',
      car_manufacturer: '',
      car_brand: '',
      vehicle_location: '',
      engine_capacity: '',
      transmission: '',
      drivetrain: '',
      steering_wheel_position: '',
      mileage: '',
      vehicle_type: '',
      condition: '',
      colour: '',
      interior_color: '',
      manufacture_year: '',
      engine: '',
      rego: '',
      sell_status: '',
      comment_status: 'closed'
    });
    setErrors({});
    setSubmitStatus(null);
    setUploadProgress(0);
    setUploadMessage('');
    setResetTrigger(prev => prev + 1);
    setHasUnsavedChanges(false);
    setShowConfirmModal(false);
    setPendingAction(null);
    setShowAdditionalFields(false);
  };

  // Handle confirmation modal
  const handleConfirmModal = () => {
    if (pendingAction === 'reset') {
      performReset();
    } else if (pendingAction === 'cancel') {
      performCancel();
    }
  };

  // Handle cancel modal
  const handleCancelModal = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await getCategories();
        setCategories(categoriesData);
      } catch (error) {
        // Fallback to default categories if API fails
        setCategories([
          { id: 1, name: 'Uncategorized', slug: 'uncategorized' },
          { id: 33, name: 'Creative', slug: 'creative' },
          { id: 40, name: 'Design', slug: 'design' }
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);


  const handleInputChange = (field, value) => {
    // Only allow numbers for mileage and engine_capacity fields
    if (field === 'mileage' || field === 'engine_capacity') {
      // Remove any non-numeric characters (allow decimal point for engine capacity)
      let numericValue;
      if (field === 'engine_capacity') {
        // Allow numbers and one decimal point
        numericValue = value.replace(/[^0-9.]/g, '');
        // Ensure only one decimal point
        const parts = numericValue.split('.');
        if (parts.length > 2) {
          numericValue = parts[0] + '.' + parts.slice(1).join('');
        }
      } else {
        // For mileage, only integers
        numericValue = value.replace(/[^0-9]/g, '');
      }
      setFormData(prev => ({
        ...prev,
        [field]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFeaturedImageChange = (file) => {
    setFormData(prev => ({
      ...prev,
      featured_image: file
    }));
  };

  const handleGalleryChange = (files) => {
    setFormData(prev => ({
      ...prev,
      gallery: files
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name_or_title.trim()) {
      newErrors.name_or_title = 'Title is required';
    }
    
    if (!formData.context.trim()) {
      newErrors.context = 'Content is required';
    }
    
    if (!formData.short_description.trim()) {
      newErrors.short_description = 'Short description is required';
    }
    
    if (!formData.content_category.trim()) {
      newErrors.content_category = 'Content category is required';
    }
    
    setErrors(newErrors);
    
    // Focus on first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      focusOnField(firstErrorField);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const focusOnField = (fieldName) => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      let element = null;
      
      switch (fieldName) {
        case 'name_or_title':
          element = document.getElementById('title');
          break;
        case 'context':
          // For ReactQuill, we need to focus on the editor
          const quillEditor = document.querySelector('.ql-editor');
          if (quillEditor) {
            quillEditor.focus();
            quillEditor.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add highlight effect to the Quill container
            const quillContainer = document.querySelector('.ql-container');
            if (quillContainer) {
              quillContainer.style.transition = 'box-shadow 0.3s ease';
              quillContainer.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.3)';
              
              setTimeout(() => {
                quillContainer.style.boxShadow = '';
              }, 2000);
            }
            return;
          }
          break;
        case 'short_description':
          element = document.getElementById('short_description');
          break;
        case 'content_category':
          element = document.getElementById('content_category');
          break;
        default:
          element = document.getElementById(fieldName);
      }
      
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add a subtle highlight effect to draw attention
        element.style.transition = 'box-shadow 0.3s ease';
        element.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.3)';
        
        // Remove the highlight after 2 seconds
        setTimeout(() => {
          element.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    setUploadProgress(0);
    setUploadMessage('');
    
    try {
      
      const result = await createPost(formData, (progress, message) => {
        setUploadProgress(progress);
        setUploadMessage(message);
      });
      
      setSuccessMessage('Post created successfully!');
      
      // Reload page after 3 seconds (modal will be visible for 3 seconds)
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      
    } catch (error) {
      if (error.message.includes('Authentication failed') || error.message.includes('401')) {
        onAuthRequired();
      }
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to create post. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
  };


  // Don't render form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Required</h3>
          <p className="text-gray-600 mb-6">Please log in to create posts.</p>
          <button
            onClick={onAuthRequired}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">August CMS</h2>
              <p className="mt-1 text-sm text-gray-600">
                Fill in the details below to create a new post on your website.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ Authenticated
              </span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Title Field */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.name_or_title}
              onChange={(e) => handleInputChange('name_or_title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.name_or_title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter post title"
            />
            {errors.name_or_title && (
              <p className="mt-1 text-sm text-red-600">{errors.name_or_title}</p>
            )}
          </div>

          {/* Short Description Field */}
          <div>
            <label htmlFor="short_description" className="block text-sm font-medium text-gray-700 mb-2">
              Short Description *
            </label>
            <textarea
              id="short_description"
              value={formData.short_description}
              onChange={(e) => handleInputChange('short_description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.short_description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter a brief description of the post"
            />
            {errors.short_description && (
              <p className="mt-1 text-sm text-red-600">{errors.short_description}</p>
            )}
          </div>

          {/* Content Category Field */}
          <div>
            <label htmlFor="content_category" className="block text-sm font-medium text-gray-700 mb-2">
              Content Category *
            </label>
            <select
              id="content_category"
              value={formData.content_category}
              onChange={(e) => handleInputChange('content_category', e.target.value)}
              disabled={loadingCategories}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.content_category ? 'border-red-300' : 'border-gray-300'
              } ${loadingCategories ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">
                {loadingCategories ? 'Loading categories...' : 'Select a category'}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category.count} posts)
                </option>
              ))}
            </select>
            {errors.content_category && (
              <p className="mt-1 text-sm text-red-600">{errors.content_category}</p>
            )}
          </div>

          {/* Content Field */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <ReactQuill
              value={formData.context}
              onChange={(value) => handleInputChange('context', value)}
              modules={quillModules}
              className={`${errors.context ? 'border-red-300' : ''}`}
            />
            {errors.context && (
              <p className="mt-1 text-sm text-red-600">{errors.context}</p>
            )}
          </div>

          {/* Featured Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            <FileUpload
              onFileSelect={handleFeaturedImageChange}
              accept="image/*"
              maxSize={5 * 1024 * 1024} // 5MB
              label="Choose featured image"
            />
            {formData.featured_image && (
              <div className="mt-2">
                <img
                  src={URL.createObjectURL(formData.featured_image)}
                  alt="Featured image preview"
                  className="h-32 w-32 object-cover rounded-md border"
                />
              </div>
            )}
          </div>

          {/* Gallery Upload */}
          <div>
            {/* Gallery Style Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Gallery Layout
              </label>
              <div className="grid grid-cols-3 gap-4">
                {/* 1 Column Option */}
                <div 
                  className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.gallery_style === '1-column'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleInputChange('gallery_style', '1-column')}
                >
                  <input
                    type="radio"
                    name="gallery_style"
                    value="1-column"
                    checked={formData.gallery_style === '1-column'}
                    onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                    className="sr-only"
                  />
                    <div className="w-12 h-8 mb-2 flex flex-col gap-1">
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">1 Column</span>
                </div>

                {/* 2 Column Option */}
                <div 
                  className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.gallery_style === '2-column'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleInputChange('gallery_style', '2-column')}
                >
                  <input
                    type="radio"
                    name="gallery_style"
                    value="2-column"
                    checked={formData.gallery_style === '2-column'}
                    onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                    className="sr-only"
                  />
                    <div className="w-12 h-8 mb-2 grid grid-cols-2 gap-1">
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">2 Column Grid</span>
                </div>

                {/* 3 Column Option */}
                <div 
                  className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    formData.gallery_style === '3-column'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleInputChange('gallery_style', '3-column')}
                >
                  <input
                    type="radio"
                    name="gallery_style"
                    value="3-column"
                    checked={formData.gallery_style === '3-column'}
                    onChange={(e) => handleInputChange('gallery_style', e.target.value)}
                    className="sr-only"
                  />
                    <div className="w-12 h-8 mb-2 grid grid-cols-3 gap-1">
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                      <div className="w-full h-2 bg-gray-400 rounded"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">3 Column Grid</span>
                </div>
              </div>
            </div>
            
            <GalleryUpload
              onFilesSelect={handleGalleryChange}
              accept="image/*"
              maxSize={5 * 1024 * 1024} // 5MB
              maxFiles={10}
              label="Choose gallery images"
              resetTrigger={resetTrigger}
            />
            
            {/* Gallery Shortcode Display */}
            {formData.gallery && formData.gallery.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gallery Shortcode (Copy and paste this into your content)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={`[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (existingShortcodes.length > 0) {
                        // If shortcodes exist, show message to remove them first
                        setCopyNotification('Remove existing shortcodes first!');
                        setTimeout(() => setCopyNotification(''), 2000);
                      } else if (!shortcodeCopied) {
                        // First click: Copy the shortcode
                        navigator.clipboard.writeText(`[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`);
                        setShortcodeCopied(true);
                        setCopyNotification('Shortcode copied! Click again to insert.');
                        setTimeout(() => setCopyNotification(''), 2000);
                      } else {
                        // Second click: Insert the shortcode
                        const shortcode = `[gallery ids="GALLERY_PLACEHOLDER" style="${formData.gallery_style}"]`;
                        
                        // Get the Quill editor instance
                        const quillEditor = document.querySelector('.ql-editor');
                        if (quillEditor) {
                          // Focus the editor first
                          quillEditor.focus();
                          
                          // Get current selection
                          const selection = window.getSelection();
                          if (selection.rangeCount > 0) {
                            const range = selection.getRangeAt(0);
                            // Insert the shortcode at cursor position
                            range.deleteContents();
                            range.insertNode(document.createTextNode(shortcode));
                            range.collapse(false);
                            selection.removeAllRanges();
                            selection.addRange(range);
                          } else {
                            // If no selection, append to end
                            quillEditor.innerHTML += shortcode;
                          }
                          
                          // Update the form data to reflect the change
                          setFormData(prev => ({
                            ...prev,
                            context: quillEditor.innerHTML
                          }));
                          
                          setCopyNotification('Gallery shortcode inserted!');
                          setTimeout(() => setCopyNotification(''), 2000);
                        }
                      }
                    }}
                    disabled={existingShortcodes.length > 0}
                    className={`px-4 py-2 rounded-md text-sm ${
                      existingShortcodes.length > 0 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : shortcodeCopied
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {existingShortcodes.length > 0 
                      ? 'Remove existing shortcodes first' 
                      : shortcodeCopied 
                        ? 'Insert Gallery' 
                        : 'Copy Shortcode'
                    }
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Click "Copy Shortcode" to copy, then click "Insert Gallery" to add it to your content.
                </p>
                {copyNotification && (
                  <div className="mt-2 text-sm text-green-600 font-medium">
                    {copyNotification}
                  </div>
                )}
              </div>
            )}

            {/* Existing Shortcodes Display */}
            {existingShortcodes.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <label className="block text-sm font-medium text-yellow-800 mb-2">
                  Existing Gallery Shortcodes ({existingShortcodes.length})
                </label>
                <p className="text-xs text-yellow-600 mb-3">
                  Remove existing shortcodes before inserting new ones.
                </p>
                {existingShortcodes.map((shortcode) => (
                  <div key={shortcode.id} className="flex items-center justify-between bg-white p-2 rounded border mb-2">
                    <code className="flex-1 text-sm text-gray-700 font-mono">
                      {shortcode.text}
                    </code>
                    <button
                      type="button"
                      onClick={() => removeShortcode(shortcode.text)}
                      className="ml-2 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {uploadMessage || 'Uploading...'}
                </span>
                <span className="text-sm text-gray-500">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Additional Fields Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
              <button
                type="button"
                onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {showAdditionalFields ? (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    Hide Additional Fields
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Show Additional Fields
                  </>
                )}
              </button>
            </div>
            
            {showAdditionalFields && (
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Car Manufacturer */}
              <div>
                <label htmlFor="car_manufacturer" className="block text-sm font-medium text-gray-700 mb-2">
                  Үйлдвэр
                </label>
                <input
                  type="text"
                  id="car_manufacturer"
                  value={formData.car_manufacturer}
                  onChange={(e) => handleInputChange('car_manufacturer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Үйлдвэр"
                />
              </div>

              {/* Car Brand */}
              <div>
                <label htmlFor="car_brand" className="block text-sm font-medium text-gray-700 mb-2">
                  Бренд
                </label>
                <input
                  type="text"
                  id="car_brand"
                  value={formData.car_brand}
                  onChange={(e) => handleInputChange('car_brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Бренд"
                />
              </div>

              {/* Vehicle Location */}
              <div>
                <label htmlFor="vehicle_location" className="block text-sm font-medium text-gray-700 mb-2">
                  Байрлал
                </label>
                <input
                  type="text"
                  id="vehicle_location"
                  value={formData.vehicle_location}
                  onChange={(e) => handleInputChange('vehicle_location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Байрлал"
                />
              </div>

              {/* Engine Capacity */}
              <div>
                <label htmlFor="engine_capacity" className="block text-sm font-medium text-gray-700 mb-2">
                  Моторын багтаамж (литр)
                </label>
                <input
                  type="text"
                  id="engine_capacity"
                  value={formData.engine_capacity}
                  onChange={(e) => handleInputChange('engine_capacity', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Моторын багтаамж (литр)"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                />
              </div>

              {/* Transmission */}
              <div>
                <label htmlFor="transmission" className="block text-sm font-medium text-gray-700 mb-2">
                  Хурдны хайрцаг
                </label>
                <select
                  id="transmission"
                  value={formData.transmission}
                  onChange={(e) => handleInputChange('transmission', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Automatic Transmission (AT)">Automatic Transmission (AT)</option>
                  <option value="Manual Transmission (MT)">Manual Transmission (MT)</option>
                  <option value="Semi-Automatic Transmission">Semi-Automatic Transmission</option>
                </select>
              </div>

              {/* Drivetrain */}
              <div>
                <label htmlFor="drivetrain" className="block text-sm font-medium text-gray-700 mb-2">
                  Хөтлөгч
                </label>
                <select
                  id="drivetrain"
                  value={formData.drivetrain}
                  onChange={(e) => handleInputChange('drivetrain', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Front-Wheel Drive (FWD)">Front-Wheel Drive (FWD)</option>
                  <option value="Rear-Wheel Drive (RWD)">Rear-Wheel Drive (RWD)</option>
                  <option value="All-Wheel Drive (AWD)">All-Wheel Drive (AWD)</option>
                  <option value="Four-Wheel Drive (4WD / 4x4)">Four-Wheel Drive (4WD / 4x4)</option>
                </select>
              </div>

              {/* Steering Wheel Position */}
              <div>
                <label htmlFor="steering_wheel_position" className="block text-sm font-medium text-gray-700 mb-2">
                  Жолооны хүрд
                </label>
                <select
                  id="steering_wheel_position"
                  value={formData.steering_wheel_position}
                  onChange={(e) => handleInputChange('steering_wheel_position', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Баруун">Баруун</option>
                  <option value="Зүүн">Зүүн</option>
                </select>
              </div>

              {/* Mileage */}
              <div>
                <label htmlFor="mileage" className="block text-sm font-medium text-gray-700 mb-2">
                  Явсан (км)
                </label>
                <input
                  type="text"
                  id="mileage"
                  value={formData.mileage}
                  onChange={(e) => handleInputChange('mileage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Явсан (км)"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>

              {/* Vehicle Type */}
              <div>
                <label htmlFor="vehicle_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Машины төрөл
                </label>
                <select
                  id="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={(e) => handleInputChange('vehicle_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Sedan">Sedan</option>
                  <option value="Hatchback">Hatchback</option>
                  <option value="Coupe">Coupe</option>
                  <option value="Sport Utility Vehicle (SUV)">Sport Utility Vehicle (SUV)</option>
                  <option value="Crossover (CUV)">Crossover (CUV)</option>
                  <option value="Station Wagon">Station Wagon</option>
                  <option value="Pickup Truck">Pickup Truck</option>
                  <option value="Minivan / MPV">Minivan / MPV</option>
                  <option value="Van">Van</option>
                  <option value="Convertible">Convertible</option>
                  <option value="Coupe-SUV">Coupe-SUV</option>
                  <option value="Sports Car / Supercar">Sports Car / Supercar</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Condition */}
              <div>
                <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-2">
                  Төлөв
                </label>
                <select
                  id="condition"
                  value={formData.condition}
                  onChange={(e) => handleInputChange('condition', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Шинэ">Шинэ</option>
                  <option value="Хуучин">Хуучин</option>
                </select>
              </div>

              {/* Colour */}
              <div>
                <label htmlFor="colour" className="block text-sm font-medium text-gray-700 mb-2">
                  Өнгө
                </label>
                <input
                  type="text"
                  id="colour"
                  value={formData.colour}
                  onChange={(e) => handleInputChange('colour', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Өнгө"
                />
              </div>

              {/* Interior Color */}
              <div>
                <label htmlFor="interior_color" className="block text-sm font-medium text-gray-700 mb-2">
                  Салоны өнгө
                </label>
                <input
                  type="text"
                  id="interior_color"
                  value={formData.interior_color}
                  onChange={(e) => handleInputChange('interior_color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Салоны өнгө"
                />
              </div>

              {/* Manufacture Year - Date Picker */}
              <div>
                <label htmlFor="manufacture_year" className="block text-sm font-medium text-gray-700 mb-2">
                  Үйлдвэрлэсэн он
                </label>
                <input
                  type="date"
                  id="manufacture_year"
                  value={formData.manufacture_year && formData.manufacture_year !== '0000-00-00' ? formData.manufacture_year : ''}
                  onChange={(e) => handleInputChange('manufacture_year', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Engine */}
              <div>
                <label htmlFor="engine" className="block text-sm font-medium text-gray-700 mb-2">
                  Хөдөлгүүр
                </label>
                <select
                  id="engine"
                  value={formData.engine}
                  onChange={(e) => handleInputChange('engine', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Бензин хөдөлгүүр">Бензин хөдөлгүүр</option>
                  <option value="Дизель хөдөлгүүр">Дизель хөдөлгүүр</option>
                  <option value="Хосолсон хөдөлгүүр">Хосолсон хөдөлгүүр</option>
                  <option value="Цахилгаан хөдөлгүүр">Цахилгаан хөдөлгүүр</option>
                  <option value="Бусад">Бусад</option>
                </select>
              </div>

              {/* Rego - Date Picker */}
              <div>
                <label htmlFor="rego" className="block text-sm font-medium text-gray-700 mb-2">
                  Бүртгэл
                </label>
                <input
                  type="date"
                  id="rego"
                  value={formData.rego && formData.rego !== '0000-00-00' ? formData.rego : ''}
                  onChange={(e) => handleInputChange('rego', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Sell Status */}
              <div>
                <label htmlFor="sell_status" className="block text-sm font-medium text-gray-700 mb-2">
                  Худалдаанд байгаа эсэх
                </label>
                <select
                  id="sell_status"
                  value={formData.sell_status}
                  onChange={(e) => handleInputChange('sell_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Сонгох</option>
                  <option value="Бэлэн">Бэлэн</option>
                  <option value="Зарагдсан">Зарагдсан</option>
                </select>
              </div>
            </div>
            )}

            {/* Comment Options Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Comment Settings</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="comments_closed"
                    name="comment_status"
                    value="closed"
                    checked={formData.comment_status === 'closed'}
                    onChange={(e) => handleInputChange('comment_status', e.target.value)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <label htmlFor="comments_closed" className="ml-3 block text-sm font-medium text-gray-700">
                    Comments Disabled (Default)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="comments_open"
                    name="comment_status"
                    value="open"
                    checked={formData.comment_status === 'open'}
                    onChange={(e) => handleInputChange('comment_status', e.target.value)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <label htmlFor="comments_open" className="ml-3 block text-sm font-medium text-gray-700">
                    Comments Enabled
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting && <div className="spinner mr-2"></div>}
              {isSubmitting ? 'Creating...' : 'Create Post'}
            </button>
          </div>

          {/* Error Messages */}
          {submitStatus && submitStatus.type === 'error' && (
            <div className="p-4 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">
                {submitStatus.message}
              </p>
            </div>
          )}
        </form>
      </div>
      

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Unsaved Changes</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You have unsaved changes. Are you sure you want to {pendingAction === 'reset' ? 'reset' : 'cancel'}? All changes will be lost.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={handleCancelModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Keep Editing
                </button>
                <button
                  onClick={handleConfirmModal}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {pendingAction === 'reset' ? 'Reset' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Success!</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  {successMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostForm;
